import React, { useState, useRef, useEffect } from "react";
import { LayoutGrid, ChevronDown, Fingerprint, Users, Building2, Calculator, WalletCards, Sparkles, Ruler, LifeBuoy, Lock } from "lucide-react";
import { useApp } from "../AppContext";

/**
 * PROGÈRE ECOSYSTEM SWITCHER — universal menu for all PROGÈRE modules.
 * Only the CURRENT_MODULE_CODE constant should change per module install.
 */
export const CURRENT_MODULE_CODE = "devis-ai";

export const PROGERE_ECOSYSTEM = [
  { code: "core", name: "CORE", fullName: "PROGÈRE CORE", description: "Centre de votre écosystème", url: "https://progere.ca", Icon: LayoutGrid, accent: "gold", sortOrder: 1 },
  { code: "punch", name: "PUNCH", fullName: "PROGÈRE PUNCH", description: "Heures, équipes et présences", url: "https://punch.progere.ca", Icon: Fingerprint, accent: "red", sortOrder: 2 },
  { code: "crm", name: "CRM", fullName: "PROGÈRE CRM", description: "Clients, ventes et opportunités", url: "https://crm.progere.ca", Icon: Users, accent: "gold", sortOrder: 3 },
  { code: "erp", name: "ERP", fullName: "PROGÈRE ERP", description: "Projets et opérations", url: "https://erp.progere.ca", Icon: Building2, accent: "red", sortOrder: 4 },
  { code: "compta", name: "COMPTA", fullName: "PROGÈRE COMPTA", description: "Comptabilité et finances", url: "https://compta.progere.ca", Icon: Calculator, accent: "gold", sortOrder: 5 },
  { code: "paie", name: "PAIE", fullName: "PROGÈRE PAIE", description: "Paies CCQ et non-CCQ", url: "https://paie.progere.ca", Icon: WalletCards, accent: "red", sortOrder: 6 },
  { code: "devis-ai", name: "DEVIS AI", fullName: "PROGÈRE DEVIS AI", description: "Soumissions intelligentes", url: "https://devis.progere.ca", Icon: Sparkles, accent: "gold", sortOrder: 7 },
  { code: "takeoff", name: "TAKE OFF", fullName: "PROGÈRE TAKE OFF", description: "Quantités et estimations", url: "https://takeoff.progere.ca", Icon: Ruler, accent: "red", sortOrder: 8 },
  { code: "support", name: "SUPPORT", fullName: "PROGÈRE SUPPORT", description: "Aide et assistance", url: "https://support.progere.ca", Icon: LifeBuoy, accent: "gold", sortOrder: 9 },
];

// The set of module codes the current company owns (Phase 2: fetched from ecosystem API).
// For now, "devis-ai" is always active if the user is signed into this app.
const OWNED_MODULES = new Set(["devis-ai"]);

export default function ProgereEcosystemMenu() {
  const { user } = useApp();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const current = PROGERE_ECOSYSTEM.find((m) => m.code === CURRENT_MODULE_CODE);
  const others = PROGERE_ECOSYSTEM.filter((m) => m.code !== CURRENT_MODULE_CODE);

  const onOpen = (mod) => {
    if (mod.code === CURRENT_MODULE_CODE) return;
    if (OWNED_MODULES.has(mod.code)) window.location.href = mod.url;
    else window.open(mod.url, "_blank", "noopener");
  };

  return (
    <div ref={rootRef} className="relative" data-testid="ecosystem-menu">
      <button onClick={() => setOpen((v) => !v)} data-state={open ? "open" : "closed"}
        aria-label="Applications PROGÈRE"
        className="inline-flex items-center gap-2 h-[38px] px-3 rounded-md text-[13px] font-semibold transition"
        style={{
          color: "var(--pg-text-primary, #F4F4F6)",
          background: open
            ? "linear-gradient(180deg, #1C1D23, #15161B)"
            : "linear-gradient(180deg, #17181D, #111216)",
          border: `1px solid ${open ? "var(--pg-gold, #F2BC2E)" : "var(--pg-border, #30323A)"}`,
          boxShadow: open ? "0 0 0 1px rgba(242,188,46,0.12), 0 8px 24px rgba(0,0,0,0.38), 0 0 18px rgba(242,188,46,0.08)" : "none",
        }}
        data-testid="ecosystem-trigger">
        <LayoutGrid size={14} />
        <span className="hidden lg:inline">Applications PROGÈRE</span>
        <ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms" }} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 z-50 rounded-[10px] overflow-hidden"
          style={{
            width: 390, maxHeight: 620,
            color: "var(--pg-text-primary, #E8E8EB)",
            background: "radial-gradient(circle at top right, rgba(242,188,46,0.05), transparent 34%), linear-gradient(180deg, #121318 0%, #090A0D 100%)",
            border: "1px solid var(--pg-border-strong, #34363E)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.62), 0 0 24px rgba(242,188,46,0.04)",
            transformOrigin: "top right",
            animation: "pgFadeSlide 180ms ease",
          }}
          data-testid="ecosystem-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-[14px] border-b" style={{ borderColor: "#292B32", minHeight: 67 }}>
            <div>
              <div className="font-black tracking-tight leading-none" style={{ fontSize: 18 }}>
                <span style={{ color: "#D4AF37" }}>PRO</span>
                <span style={{ color: "#C00000" }}>GÈRE</span>
              </div>
              <div className="mt-[7px] font-extrabold uppercase" style={{ color: "#9799A1", fontSize: 8, letterSpacing: "2.6px" }}>
                VOTRE ÉCOSYSTÈME D'ENTREPRISE
              </div>
            </div>
            {user?.name && (
              <div className="w-9 h-9 rounded-full flex items-center justify-center font-black text-xs"
                style={{ background: "linear-gradient(180deg, #F5D062, #D4AF37)", color: "#0B0B0B" }}
                title={user.company || user.name}>
                {(user.company || user.name).slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>

          {/* Current module */}
          <div className="px-3 pt-3 pb-1 text-[9px] font-black uppercase tracking-[2.4px]" style={{ color: "#8F9199" }}>MODULE ACTUEL</div>
          {current && (
            <div className="mx-3 mb-3 rounded-[9px] relative"
              style={{
                display: "grid", gridTemplateColumns: "46px 1fr auto", alignItems: "center", gap: 11,
                padding: "11px 12px",
                border: "1px solid rgba(242,188,46,0.42)",
                background: "linear-gradient(135deg, rgba(45,36,10,0.72), rgba(15,13,8,0.94))",
                boxShadow: "inset 0 0 22px rgba(242,188,46,0.025), 0 7px 18px rgba(0,0,0,0.25)",
              }}
              data-testid="ecosystem-current">
              <div className="w-[42px] h-[42px] rounded-lg flex items-center justify-center" style={{ background: "rgba(242,188,46,0.14)", border: "1px solid rgba(242,188,46,0.35)" }}>
                <current.Icon size={22} color="#F2BC2E" />
              </div>
              <div>
                <div className="font-black" style={{ color: "#F2BC2E", fontSize: 13 }}>{current.fullName}</div>
                <div className="mt-[3px]" style={{ color: "#A5A6AC", fontSize: 11 }}>{current.description}</div>
              </div>
              <div className="px-[7px] py-[5px] rounded font-black uppercase" style={{ background: "#F2BC2E", color: "#08090B", fontSize: 8, letterSpacing: 1 }}>
                ACTIF
              </div>
            </div>
          )}

          {/* Other modules */}
          <div className="px-3 pt-1 pb-2 text-[9px] font-black uppercase tracking-[2.4px]" style={{ color: "#8F9199" }}>VOS APPLICATIONS</div>
          <div className="grid grid-cols-2 gap-2 px-3 pb-3" style={{ maxHeight: 400, overflowY: "auto" }}>
            {others.map((mod) => {
              const isOwned = OWNED_MODULES.has(mod.code);
              return (
                <button key={mod.code} onClick={() => onOpen(mod)}
                  className="relative rounded-lg text-left transition hover:-translate-y-0.5"
                  style={{
                    minHeight: 92, padding: 11,
                    color: "#E7E7EA", opacity: isOwned ? 1 : 0.72,
                    background: "linear-gradient(180deg, #17181D 0%, #101115 100%)",
                    border: "1px solid #292B32",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(242,188,46,0.38)"; e.currentTarget.style.background = "linear-gradient(180deg, #1C1D23 0%, #131419 100%)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#292B32"; e.currentTarget.style.background = "linear-gradient(180deg, #17181D 0%, #101115 100%)"; }}
                  data-testid={`ecosystem-module-${mod.code}`}>
                  {!isOwned && (
                    <Lock size={12} color="#F2BC2E" className="absolute top-[10px] right-[10px]" />
                  )}
                  <div className="w-[30px] h-[30px] rounded-md flex items-center justify-center" style={{ background: mod.accent === "gold" ? "rgba(242,188,46,0.12)" : "rgba(241,13,25,0.12)" }}>
                    <mod.Icon size={16} color={mod.accent === "gold" ? "#F2BC2E" : "#F10D19"} />
                  </div>
                  <div className="mt-2 font-black uppercase" style={{ color: "#F4F4F6", fontSize: 11 }}>{mod.name}</div>
                  <div className="mt-[3px]" style={{ color: "#8F9199", fontSize: 9, lineHeight: 1.35 }}>{mod.description}</div>
                  <div className="mt-2 inline-block px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-widest"
                    style={{ background: isOwned ? "rgba(25,199,140,0.15)" : "rgba(242,188,46,0.12)", color: isOwned ? "#19C78C" : "#F2BC2E" }}>
                    {isOwned ? "ACTIF" : "DÉCOUVRIR"}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pgFadeSlide {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
